<template>
  <div>
    <div class="search-container">
      <SearchInput
        class="search-bar"
        name="filter-search"
        v-model="searchTerm"
        placeholder="Search filter name or keyword"
      ></SearchInput>
    </div>
    <div>
      <div v-if="!shopperIntelligenceEmpty" class="title">
        <PersonIcon />
        <div>Shopper Intelligence</div>
      </div>
      <FilterAccordion
        v-for="(dimensions, category) in shopperIntelligenceCategoryMap"
        :class="accordionClass"
        :key="category"
        :value="shopperIntelligenceValuesMap[category]"
        :category="category"
        :dimensions="dimensions"
        :dateRange="dateRange"
        :brandIds="brandIds"
        :portfolioBrands="portfolioBrands"
        :defaultLimit="defaultLimit"
        :displayLimit="displayLimit"
        :fetchDemographicsValues="true"
        :openAccordian="searchTerm !== ''"
        :hasBrandError="hasBrandError"
        @error="$emit('error', $event)"
        @input="updateValue"
      ></FilterAccordion>
    </div>
    <div>
      <div v-if="!insightsIntelligenceEmpty" class="title">
        <BarChartIcon />
        <div>MikMak Insights</div>
      </div>
      <FilterAccordion
        v-for="(dimensions, category) in insightsCategoryMap"
        :class="accordionClass"
        :key="category"
        :value="insightsValuesMap[category]"
        :category="category"
        :dimensions="dimensions"
        :dateRange="dateRange"
        :brandIds="brandIds"
        :portfolioBrands="portfolioBrands"
        :defaultLimit="defaultLimit"
        :displayLimit="displayLimit"
        :openAccordian="searchTerm !== ''"
        :hasBrandError="hasBrandError"
        @error="$emit('error', $event)"
        @input="updateValue"
      ></FilterAccordion>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import {
  SearchInput,
  PersonIcon,
  BarChartIcon,
} from '@mikmak/components';
import FilterAccordion from '../../../../../Common/FilterAccordion.vue';

export default {
  props: {
    availableShopperIntelligenceFilters: {
      type: Array,
      required: true,
    },
    availableInsightsFilters: {
      type: Array,
      required: true,
    },
    selectedFilters: {
      type: Array,
      required: true,
    },
    portfolioBrands: Array,
    dateRange: Object,
    brandIds: Array,
  },
  data() {
    return {
      searchTerm: '',
      filtersLocalValues: this.selectedFilters,
      shopperIntelligenceShowingAll: {},
      defaultLimit: 10,
      displayLimit: 1,
    };
  },
  methods: {
    selectFilterValue(category, value) {
      category.selected = value;
    },
    isDimensionInSearch(dimension) {
      return this.searchContains(dimension.category) || this.searchContains(dimension.key);
    },
    searchContains(str) {
      return str.toLowerCase().indexOf(this.searchTerm.toLowerCase()) >= 0;
    },
    getSelectedValue(dimension) {
      return this.filtersLocalValues.reduce((selected, { key, value }) => {
        if (dimension.value !== key) {
          return selected;
        }

        const filterValue = value === undefined ? [null] : value;
        const filterDisplay = filterValue.map(v => {
          const isUndefinedOrNull = v === undefined || v === null;

          return {
            key: isUndefinedOrNull ? 'null' : v,
            value: v,
          };
        });

        return selected.concat(filterDisplay);
      }, []);
    },
    updateValue(filterValuesMap) {
      const newFilters = this.getFiltersFromMap(filterValuesMap);

      for (const newFilter of newFilters) {
        const isAlreadyPresent = this.filtersLocalValues.some(
          filter => this.isFiltersEqual(filter, newFilter),
        );
        const isNewFilterValue = newFilter.value.length > 0;

        if (isAlreadyPresent) {
          const filterFunc = isNewFilterValue ? this.addFilter : this.removeFilter;

          this.filtersLocalValues = this.filtersLocalValues.reduce((filters, filter) => {
            return filterFunc(filters, filter, newFilter);
          }, []);

          continue;
        }

        if (isNewFilterValue) {
          this.filtersLocalValues = this.filtersLocalValues.concat(newFilter);
        }
      }

      this.$emit('updateFilters', this.filtersLocalValues);
    },
    isFiltersEqual(f1, f2) {
      return f1.key === f2.key;
    },
    getFiltersFromMap(filterValuesMap) {
      return Object.keys(filterValuesMap).map(key => {
        const filterValuePairs = filterValuesMap[key];
        const filterValues = filterValuePairs && filterValuePairs.length
          ? filterValuePairs.map(({ value }) => value)
          : [];

        return {
          key: key,
          value: filterValues,
          operator: 'includes',
        };
      });
    },
    addFilter(filters, currentFilter, newFilter) {
      const addFilter = this.isFiltersEqual(currentFilter, newFilter) ? newFilter : currentFilter;

      return filters.concat(addFilter);
    },
    removeFilter(filters, currentFilter, newFilter) {
      return this.isFiltersEqual(currentFilter, newFilter)
        ? filters
        : filters.concat(currentFilter);
    },
    toggleShopperIntelligence(category) {
      this.shopperIntelligenceShowingAll[category] = !this.shopperIntelligenceShowingAll[category];
    },
    showingAllCategory(category) {
      return !!this.shopperIntelligenceShowingAll[category];
    },
  },
  computed: {
    ...mapState({
      showNewSelect: state => state.main.showNewSelect,
    }),
    hasBrandError() {
      return this.brandIds && this.brandIds.length === 0;
    },
    shopperIntelligenceFiltersFiltered() {
      return this.availableShopperIntelligenceFilters
        .filter(this.isDimensionInSearch);
    },
    insightsFiltersFiltered() {
      return this.availableInsightsFilters
        .filter(this.isDimensionInSearch);
    },
    shopperIntelligenceCategoryMap() {
      return this.shopperIntelligenceFiltersFiltered.reduce((map, d) => {
        map[d.category] = map[d.category] ? map[d.category].concat([d]) : [d];

        return map;
      }, {});
    },
    insightsCategoryMap() {
      return this.insightsFiltersFiltered.reduce((map, d) => {
        map[d.category] = map[d.category] ? map[d.category].concat([d]) : [d];

        return map;
      }, {});
    },
    shopperIntelligenceValuesMap() {
      return this.availableShopperIntelligenceFilters.reduce((map, d) => {
        const individualMap = map[d.category] || {};

        individualMap[d.value] = this.getSelectedValue(d);
        map[d.category] = individualMap;

        return map;
      }, {});
    },
    insightsValuesMap() {
      return this.availableInsightsFilters.reduce((map, d) => {
        const individualMap = map[d.category] || {};

        individualMap[d.value] = this.getSelectedValue(d);
        map[d.category] = individualMap;

        return map;
      }, {});
    },
    shopperIntelligenceEmpty() {
      return !this.shopperIntelligenceFiltersFiltered ||
        !this.shopperIntelligenceFiltersFiltered.length;
    },
    insightsIntelligenceEmpty() {
      return !this.insightsFiltersFiltered ||
        !this.insightsFiltersFiltered.length;
    },
    accordionClass() {
      return this.showNewSelect
        ? 'accordion-container'
        : 'accordion-container-old-select';
    },
  },
  watch: {
    selectedFilters() {
      this.filtersLocalValues = this.selectedFilters;
    },
  },
  components: {
    SearchInput,
    PersonIcon,
    BarChartIcon,
    FilterAccordion,
  },
};
</script>
<style lang="scss" scoped>
  .search-container {
    width: 100%;
  }
  .title {
    display: flex;
    flex-direction: row;
    font-style: normal;
    font-weight: bold;
    font-size: 18px;
    line-height: 140%;
    color: #223448;
    padding-top: 24px;
    padding-bottom: 24px;
  }
  .accordion-container {
    margin-bottom: 16px;
    display: inline-block;
    width: 100%;
    ::v-deep {
      & .multi-select {
        margin-right: 16px;
        width: calc(50% - 16px);
        float: left;
      }
      & .accordion-content {
        padding: 0px;
        padding-left: 16px;
      }
      & .more-less-option {
        width: 100%;
        padding-top: 16px;
      }
    }
  }
  .accordion-container-old-select {
    margin-bottom: 16px;
    position: relative;
    ::v-deep {
      & .multi-select-wrapper {
        margin-right: 16px;
        width: calc(50% - 16px);
      }
      & .multi-select-wrapper input[type="text"] {
        padding-top: 16px;
      }
      & .accordion-content {
        padding: 0px;
        padding-left: 16px;
      }
      & .multi-select-wrapper .badge-wrapper.selected-badge button.secondary,
      & .accordion-header .badge-wrapper.selected-badge.header button.secondary
       {
        background: #4369F4;
      }
    }
  }

</style>
